import React, { useEffect, useState } from "react";
import {
    ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    Legend, ResponsiveContainer
} from "recharts";
import { Loader2 } from "lucide-react";
import useUserStore from "../../stores/userStore";
import { fetchEsstReport } from "../../services/payrollService";

const CumulativeHoursChart = () => {
    const { currentUser } = useUserStore();
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [year, setYear] = useState(new Date().getFullYear());

    useEffect(() => {
        if (!currentUser?.user_id) return;

        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const report = await fetchEsstReport(currentUser.user_id, year);
                if (!cancelled) {
                    setData(report.days || []);
                    setSummary(report.summary || null);
                }
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [currentUser?.user_id, year]);

    if (loading) {
        return (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60 p-6">
                <div className="flex items-center justify-center py-12 text-gray-400 gap-2">
                    <Loader2 size={20} className="animate-spin" /> Loading hours data...
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60 p-6">
                <div className="text-center py-8 text-red-500 text-sm">{error}</div>
            </div>
        );
    }

    if (!data.length) {
        return (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60 p-6">
                <div className="text-center py-8 text-gray-400 text-sm">No timesheet data for {year}.</div>
            </div>
        );
    }

    const chartData = data.map(d => ({
        date: d.date,
        hoursWorked: d.cumulative_hours,
        sickHours: (d.cumulative_sick_used || 0) + (d.cumulative_unprotected || 0),
        esstBank: d.esst_bank ?? 0,
    }));

    const formatDate = (d) => {
        const dt = new Date(d + "T00:00:00");
        return `${dt.getMonth() + 1}/${dt.getDate()}`;
    };

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60 p-6">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-sm font-bold text-gray-800">Cumulative Hours — {year}</h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                        Hours worked vs sick time used over the year
                    </p>
                </div>
                <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-xl px-1">
                    <button
                        onClick={() => setYear(y => y - 1)}
                        className="!p-1 !bg-transparent !border-none hover:!bg-gray-100 !rounded !text-gray-500 !transition-colors"
                    >
                        ‹
                    </button>
                    <span className="px-2 text-xs font-semibold text-gray-600">{year}</span>
                    <button
                        onClick={() => setYear(y => y + 1)}
                        disabled={year >= new Date().getFullYear()}
                        className="!p-1 !bg-transparent !border-none hover:!bg-gray-100 !rounded !text-gray-500 disabled:!opacity-30 !transition-colors"
                    >
                        ›
                    </button>
                </div>
            </div>

            {/* Summary stat pills */}
            {summary && (
                <div className="flex flex-wrap gap-3 mb-4">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 border border-indigo-100 rounded-lg">
                        <div className="w-2 h-2 rounded-full bg-indigo-500" />
                        <span className="text-xs font-semibold text-indigo-700">{summary.total_hours} hrs worked</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 border border-rose-100 rounded-lg">
                        <div className="w-2 h-2 rounded-full bg-rose-500" />
                        <span className="text-xs font-semibold text-rose-700">{summary.total_sick_used} hrs sick</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-100 rounded-lg">
                        <div className="w-2 h-2 rounded-full bg-emerald-500" />
                        <span className="text-xs font-semibold text-emerald-700">{summary.esst_bank} hrs ESST bank</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg">
                        <span className="text-xs font-semibold text-gray-600">{summary.days_worked} days worked</span>
                    </div>
                </div>
            )}

            <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={chartData}>
                    <defs>
                        <linearGradient id="gradHours" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gradSick" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11 }}
                        tickFormatter={formatDate}
                    />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 11, fill: "#10b981" }}
                        label={{ value: "ESST Bank (hrs)", angle: 90, position: "insideRight", style: { fontSize: 10, fill: "#10b981" } }}
                    />
                    <Tooltip
                        labelFormatter={(d) => d}
                        formatter={(value, name) => [
                            `${Number(value).toFixed(1)} hrs`,
                            name
                        ]}
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="hoursWorked"
                        name="Hours Worked"
                        stroke="#6366f1"
                        strokeWidth={2}
                        fill="url(#gradHours)"
                        dot={false}
                        activeDot={{ r: 4 }}
                    />
                    <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="sickHours"
                        name="Sick Hours"
                        stroke="#f43f5e"
                        strokeWidth={2}
                        fill="url(#gradSick)"
                        dot={false}
                        activeDot={{ r: 4 }}
                    />
                    <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="esstBank"
                        name="ESST Bank"
                        stroke="#10b981"
                        strokeWidth={2}
                        strokeDasharray="6 3"
                        dot={false}
                        activeDot={{ r: 4, fill: "#10b981" }}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
};

export default React.memo(CumulativeHoursChart);
